/** Falling-blocks puzzle logic. Pure and deterministic given an RNG — no DOM. */
export const COLS = 10;
export const ROWS = 18;

export type Cell = string | 0;
export type Shape = number[][];

export const PIECES: Record<string, { shape: Shape; color: string }> = {
  I: { shape: [[0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0], [0, 0, 0, 0]], color: '#38d6d8' },
  J: { shape: [[1, 0, 0], [1, 1, 1], [0, 0, 0]], color: '#5a8dff' },
  L: { shape: [[0, 0, 1], [1, 1, 1], [0, 0, 0]], color: '#ff9f43' },
  O: { shape: [[1, 1], [1, 1]], color: '#ffd34e' },
  S: { shape: [[0, 1, 1], [1, 1, 0], [0, 0, 0]], color: '#3fd38a' },
  T: { shape: [[0, 1, 0], [1, 1, 1], [0, 0, 0]], color: '#a66bff' },
  Z: { shape: [[1, 1, 0], [0, 1, 1], [0, 0, 0]], color: '#ff6b6b' },
};
const KEYS = Object.keys(PIECES);
const LINE_POINTS = [0, 100, 300, 500, 800];

export interface Piece {
  key: string;
  shape: Shape;
  color: string;
  x: number;
  y: number;
}

export function rotateCW(m: Shape): Shape {
  const n = m.length;
  return m.map((row, i) => row.map((_, j) => m[n - 1 - j][i]));
}

export class BlocksGame {
  board: Cell[][] = [];
  current: Piece | null = null;
  nextKey = 'T';
  score = 0;
  lines = 0;
  level = 1;
  over = false;
  private bag: string[] = [];
  /** Last clear count, consumed by the UI for feedback. */
  onClear: (n: number) => void = () => {};

  constructor(private rng: () => number = Math.random) {
    this.reset();
  }

  reset(): void {
    this.board = Array.from({ length: ROWS }, () => Array<Cell>(COLS).fill(0));
    this.score = 0;
    this.lines = 0;
    this.level = 1;
    this.over = false;
    this.bag = [];
    this.nextKey = this.draw();
    this.spawn();
  }

  /** 7-bag randomizer: every piece appears once per 7, so no long droughts. */
  private draw(): string {
    if (!this.bag.length) {
      this.bag = [...KEYS];
      for (let i = this.bag.length - 1; i > 0; i--) {
        const j = Math.floor(this.rng() * (i + 1));
        [this.bag[i], this.bag[j]] = [this.bag[j], this.bag[i]];
      }
    }
    return this.bag.pop()!;
  }

  get dropInterval(): number {
    return Math.max(110, 800 - (this.level - 1) * 65);
  }

  collides(x: number, y: number, shape: Shape): boolean {
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[r].length; c++) {
        if (!shape[r][c]) continue;
        const nx = x + c, ny = y + r;
        if (nx < 0 || nx >= COLS || ny >= ROWS) return true;
        if (ny >= 0 && this.board[ny][nx]) return true;
      }
    }
    return false;
  }

  private spawn(): void {
    const key = this.nextKey;
    this.nextKey = this.draw();
    const p = PIECES[key];
    this.current = { key, shape: p.shape.map((r) => [...r]), color: p.color, x: Math.floor((COLS - p.shape[0].length) / 2), y: key === 'I' ? -1 : 0 };
    if (this.collides(this.current.x, this.current.y, this.current.shape)) {
      this.over = true;
      this.current = null;
    }
  }

  move(dx: number): boolean {
    const p = this.current;
    if (!p || this.over || this.collides(p.x + dx, p.y, p.shape)) return false;
    p.x += dx;
    return true;
  }

  /** Rotate with simple wall kicks (0, ±1, ±2 columns, then one row up). */
  rotate(): boolean {
    const p = this.current;
    if (!p || this.over) return false;
    const r = rotateCW(p.shape);
    for (const [kx, ky] of [[0, 0], [-1, 0], [1, 0], [-2, 0], [2, 0], [0, -1]]) {
      if (!this.collides(p.x + kx, p.y + ky, r)) {
        p.shape = r;
        p.x += kx;
        p.y += ky;
        return true;
      }
    }
    return false;
  }

  /** One row down; locks the piece when it can't move. Returns lines cleared on lock, or -1 if it just moved. */
  softDrop(manual = false): number {
    const p = this.current;
    if (!p || this.over) return -1;
    if (!this.collides(p.x, p.y + 1, p.shape)) {
      p.y++;
      if (manual) this.score += 1;
      return -1;
    }
    return this.lock();
  }

  hardDrop(): number {
    const p = this.current;
    if (!p || this.over) return 0;
    let n = 0;
    while (!this.collides(p.x, p.y + 1, p.shape)) {
      p.y++;
      n++;
    }
    this.score += n * 2;
    return this.lock();
  }

  ghostY(): number {
    const p = this.current;
    if (!p) return 0;
    let y = p.y;
    while (!this.collides(p.x, y + 1, p.shape)) y++;
    return y;
  }

  private lock(): number {
    const p = this.current!;
    p.shape.forEach((row, r) =>
      row.forEach((v, c) => {
        const by = p.y + r, bx = p.x + c;
        if (v && by >= 0 && by < ROWS) this.board[by][bx] = p.color;
      }),
    );
    const cleared = this.clearLines();
    this.spawn();
    return cleared;
  }

  private clearLines(): number {
    const kept = this.board.filter((row) => row.some((c) => c === 0));
    const cleared = ROWS - kept.length;
    if (!cleared) return 0;
    this.board = [...Array.from({ length: cleared }, () => Array<Cell>(COLS).fill(0)), ...kept];
    this.lines += cleared;
    this.score += LINE_POINTS[cleared] * this.level;
    this.level = Math.floor(this.lines / 8) + 1;
    this.onClear(cleared);
    return cleared;
  }
}
